import { type ReactNode, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ArrowLeft } from 'lucide-react'
import { VerifiedBadge } from '@/components/VerifiedBadge'
import type { AdminOrganizationDetail, AdminEvent } from '@/types'

type Props = {
  organization: AdminOrganizationDetail
  events: AdminEvent[]
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return 'Fundraiser'
  const d = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function AdminOrganizationShow({ organization, events }: Props) {
  const [deleteOpen, setDeleteOpen] = useState(false)

  function toggleVerified() {
    router.patch(`/admin/organizations/${organization.id}`, {
      verified: organization.verified ? 'false' : 'true'
    })
  }

  function togglePlan() {
    router.patch(`/admin/organizations/${organization.id}`, {
      plan: organization.pro ? 'free' : 'pro'
    })
  }

  function toggleFeatured() {
    router.patch(`/admin/organizations/${organization.id}`, {
      featured: organization.featured ? 'false' : 'true'
    })
  }

  function confirmDelete() {
    router.delete(`/admin/organizations/${organization.id}`)
    setDeleteOpen(false)
  }

  return (
    <>
      <Head title={`Admin - ${organization.name}`} />
      <div>
        <Link href="/admin/organizations" className="inline-flex items-center gap-1 text-base text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="size-4" />
          Back to organizations
        </Link>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight font-serif">{organization.name}</h1>
            <p className="text-muted-foreground mt-1">{organization.email}</p>
          </div>
          <div className="flex gap-2">
            {organization.verified ? (
              <VerifiedBadge variant="pill" />
            ) : (
              <Badge variant="outline" className="text-muted-foreground">Unverified</Badge>
            )}
            <Badge variant={organization.pro ? 'default' : 'secondary'}>
              {organization.pro ? 'Pro' : 'Free'}
            </Badge>
          </div>
        </div>

        {/* Detail Card */}
        <div className="mt-6 rounded-2xl bg-muted/30 ring-1 ring-black/5 p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Detail label="Slug" value={organization.slug} />
            <Detail label="Website" value={organization.website} />
            <Detail label="Phone" value={organization.phone} />
            <Detail label="Primary Cause" value={organization.primary_cause} />
            <Detail label="Events" value={String(organization.events_count)} />
            <Detail label="Followers" value={String(organization.followers_count)} />
            <Detail label="EIN" value={organization.ein} />
            <Detail label="Stripe Status" value={organization.stripe_subscription_status || 'None'} />
            <Detail label="Verification Domain" value={organization.verification_domain} />
            <Detail label="Verification Email" value={organization.verification_email} />
            <Detail label="Created" value={formatDate(organization.created_at)} />
          </div>
          {organization.causes.length > 0 && (
            <div>
              <p className="text-base text-muted-foreground font-medium mb-1">Causes</p>
              <div className="flex flex-wrap gap-1">
                {organization.causes.map(cause => (
                  <Badge key={cause} variant="secondary">{cause}</Badge>
                ))}
              </div>
            </div>
          )}
          {organization.description && (
            <div>
              <p className="text-base text-muted-foreground font-medium mb-1">Description</p>
              <p className="text-base">{organization.description}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex gap-3">
          <Button onClick={toggleVerified} variant={organization.verified ? 'outline' : 'default'}>
            {organization.verified ? 'Unverify Organization' : 'Verify Organization'}
          </Button>
          <Button onClick={togglePlan} variant="outline">
            {organization.pro ? 'Downgrade to Free' : 'Upgrade to Pro'}
          </Button>
          <Button onClick={toggleFeatured} variant={organization.featured ? 'secondary' : 'outline'}>
            {organization.featured ? 'Unfeature Organization' : 'Feature Organization'}
          </Button>
        </div>

        {/* Events */}
        <div className="mt-10">
          <h2 className="text-xl font-semibold tracking-tight font-serif">Events</h2>
          {events.length > 0 ? (
            <Table className="mt-4">
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Page Views</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map(event => (
                  <TableRow key={event.id}>
                    <TableCell className="font-medium">{event.title}</TableCell>
                    <TableCell>{formatDate(event.date)}</TableCell>
                    <TableCell>
                      <Badge variant={event.status === 'published' ? 'default' : 'secondary'}>
                        {event.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{event.page_views.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="mt-4 text-base text-muted-foreground">No events yet.</p>
          )}
        </div>

        {/* Danger Zone */}
        <div className="mt-10 rounded-2xl border border-destructive/30 bg-destructive/5 p-6">
          <h2 className="text-xl font-semibold tracking-tight font-serif text-destructive">Danger Zone</h2>
          <p className="mt-2 text-base text-muted-foreground">
            Permanently delete this organization, its {organization.events_count} event{organization.events_count === 1 ? '' : 's'}, {organization.followers_count} follower link{organization.followers_count === 1 ? '' : 's'}, and any active Stripe subscription. Users remain, but lose access. This cannot be undone.
          </p>
          <Button variant="destructive" className="mt-4" onClick={() => setDeleteOpen(true)}>
            Delete Organization
          </Button>
        </div>
      </div>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {organization.name}?</DialogTitle>
            <DialogDescription>
              This permanently deletes the organization, its {organization.events_count} event{organization.events_count === 1 ? '' : 's'}, member links, and cancels any active Stripe subscription. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function Detail({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <p className="text-base text-muted-foreground font-medium">{label}</p>
      <p className="text-base mt-0.5">{value || '—'}</p>
    </div>
  )
}

AdminOrganizationShow.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
